/* =========================================================
   TF CART DRAWER
   - Opens drawer on AJAX add-to-cart (carousel + PDP) WITHOUT hijacking buttons
   - Renders items with qty - / +, manual qty input, and Remove
   - Updates cart via /cart/change.js and re-renders drawer
========================================================= */

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.getElementById('tf-cart-drawer');
  const content = drawer?.querySelector('[data-cart-content]');
  if (!drawer || !content) return;

  /* =========================
     OPEN / CLOSE
  ========================= */
  const openDrawer = () => {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('tf-no-scroll');
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('tf-no-scroll');
  };

  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  /* =========================
     HELPERS
  ========================= */
  const escapeHtml = (str) =>
    String(str ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents);
      } catch (_) {}
    }
    return (Number(cents || 0) / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'CAD'
    });
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  };

  const changeLine = async (line, quantity) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!res.ok) throw new Error('Cart change failed');
    return res.json();
  };

  /* =========================
     RENDER CART
  ========================= */
  const renderCart = (cart) => {
    if (!cart?.items?.length) {
      content.innerHTML = `<p style="padding:14px 0; opacity:.7;">Your cart is empty.</p>`;
      return;
    }

    content.innerHTML = cart.items
      .map((item, idx) => {
        const line = idx + 1; // change.js uses 1-based "line"
        const title = escapeHtml(item.product_title || item.title);
        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? escapeHtml(item.variant_title)
            : '';
        const img = item.image ? `<img src="${item.image}" alt="${title}" loading="lazy">` : '';
        const linePrice = formatMoney(item.final_line_price ?? item.price * item.quantity);

        return `
          <div class="tf-mini-item" data-line="${line}"
               style="display:grid; grid-template-columns: 92px 1fr; gap:14px; padding:30px 0; border-bottom:1px solid var(--color-border);">

            <div class="tf-mini-item__img"
                 style="width:92px; height:92px; overflow:hidden; border-radius:10px; background:rgba(0,0,0,0.04);">
              ${img}
            </div>

            <div class="tf-mini-item__body" style="min-width:0;">
              <div class="tf-mini-item__top" style="display:flex; flex-direction: column; justify-content:space-between; gap:6px;">
                <div class="tf-mini-item__text" style="min-width:0;">
                  <p style="margin:0; font-size: 16px; font-weight:650; line-height:1.25; color: var(--color-primary);">${title}</p>
                  ${variant ? `<p style="margin:6px 0 0; opacity:.7; font-size:14px;">${variant}</p>` : ''}
                </div>
                <div style="font-weight:800; white-space:nowrap; color: var(--color-primary);">${linePrice}</div>
              </div>

              <div class="tf-mini-item__actions"
                   style="margin-top:12px; display:flex; align-items:center; justify-content:space-between; gap:12px;">
                
                <div class="tf-mini-qty"
                     style="display:inline-grid; grid-template-columns:44px 56px 44px;
                            border:1px solid var(--color-primary); border-radius:10px; overflow:hidden;
                            background: var(--color-background);">
                  <button type="button" class="tf-mini-qty__btn" data-qty-minus aria-label="Decrease quantity"
                          style="border:0; background:transparent; cursor:pointer; font-size:20px; line-height:1;
                                 color: var(--color-primary); display:grid; place-items:center;">
                    −
                  </button>

                  <input class="tf-mini-qty__input" type="number" min="0" value="${item.quantity}" data-qty-input
                         style="border:0; text-align:center; width:56px; padding:10px 0; background:transparent;
                                color: var(--color-primary); font-weight:650; -moz-appearance:textfield;" />

                  <button type="button" class="tf-mini-qty__btn" data-qty-plus aria-label="Increase quantity"
                          style="border:0; background:transparent; cursor:pointer; font-size:20px; line-height:1;
                                 color: var(--color-primary); display:grid; place-items:center;">
                    +
                  </button>
                </div>

                <button type="button" class="tf-mini-remove" data-remove
                        style="background:none; border:0; padding:0; cursor:pointer; text-decoration:underline;
                               opacity:.75; color: var(--color-primary); font-size:14px;">
                  Remove
                </button>

              </div>
            </div>
          </div>
        `;
      })
      .join('');
  };

  const refreshDrawer = async (open = false) => {
    const cart = await fetchCart();
    renderCart(cart);
    if (open) openDrawer();
  };

  /* =========================================================
     ✅ FIX: DO NOT HIJACK BUTTONS
     We OPEN drawer after ANY successful /cart/add fetch,
     without preventDefault/stopPropagation.
  ========================================================= */

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const res = await originalFetch(...args);

    try {
      const url = String(args?.[0] ?? '');
      const opts = args?.[1] || {};
      const method = String(opts.method || 'GET').toUpperCase();

      const isCartAdd =
        method === 'POST' &&
        (url.includes('/cart/add') || url.includes('/cart/add.js'));

      if (isCartAdd && res.ok) {
        // wait slightly so Shopify cart updates everywhere
        setTimeout(() => {
          refreshDrawer(true).catch(console.error);
        }, 80);
      }
    } catch (e) {
      // do nothing
    }

    return res;
  };

  /* =========================
     DRAWER QTY + REMOVE (delegated)
  ========================= */
  content.addEventListener('click', async (e) => {
    const itemEl = e.target.closest('.tf-mini-item');
    if (!itemEl) return;

    const line = Number(itemEl.dataset.line);
    const input = itemEl.querySelector('[data-qty-input]');
    const current = Number(input?.value || 0);

    try {
      if (e.target.closest('[data-remove]')) {
        await changeLine(line, 0);
        await refreshDrawer(false);
        return;
      }

      if (e.target.closest('[data-qty-minus]')) {
        const next = Math.max(0, current - 1);
        await changeLine(line, next);
        await refreshDrawer(false);
        return;
      }

      if (e.target.closest('[data-qty-plus]')) {
        const next = current + 1;
        await changeLine(line, next);
        await refreshDrawer(false);
        return;
      }
    } catch (err) {
      console.error(err);
    }
  });

  // Manual qty input typing
  content.addEventListener('change', async (e) => {
    if (!e.target.matches('[data-qty-input]')) return;

    const itemEl = e.target.closest('.tf-mini-item');
    if (!itemEl) return;

    const line = Number(itemEl.dataset.line);
    const next = Math.max(0, Number(e.target.value || 0));

    try {
      await changeLine(line, next);
      await refreshDrawer(false);
    } catch (err) {
      console.error(err);
    }
  });

  // Optional: render once on page load
  // refreshDrawer(false);
});
